// Months — is a month closed, reconciled, or what is blocking it? (G8)
//
// Read-only. Every fact comes from the Worker's GET /v1/admin/{company}/months
// response; this page derives no status of its own. In particular:
//   * a blocker's route is the server-sent `target`, never the key
//   * a blocked statement can be a statement check AND a review card, so the
//     two counts are shown as two lines and never added together
//   * a closed month can still carry a saving statement check: it is shown
//   * a month waiting only on the hub says so, not "needs you"
//   * for the account manager, a month blocked only by cards the owner holds
//     says it is waiting for the owner, not "needs you"
//   * a status or target this page does not know is shown neutrally, never as
//     a blank chip, an "undefined" label or a button that goes nowhere
//   * undated blockers are shown above the list, labelled as month-less
//   * an "as of" line appears only when the server sent booksAsOf

import { FLEET_QUEUE_LIST_LIMIT, type AdminClient } from "../api.js";
import { ConsoleDataError } from "../console/data-source.js";
import {
  MONTH_BLOCKER_KEYS,
  type AdminMonth,
  type AdminMonthBlocker,
  type AdminMonthsLastBookingRun,
  type AdminMonthsResponse,
  type MonthBlockerKey,
  type MonthBlockerTarget,
  type MonthReconciled,
  type MonthStatus,
} from "../console/types.js";
import { clear, h } from "../dom.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { button, chip, pageHeader, svgIcon } from "../ui/components.js";
import {
  accountLabelSet,
  dateText,
  timeAgoText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { monthAction, readerRole } from "../ui/facts.js";
import { failureCopy } from "../ui/failure.js";
import { navigateTo } from "../ui/nav-bus.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { formatPeriodLabel } from "../ui/period-control.js";
import { setReviewScopeHint, type ReviewCardSet } from "../ui/review-scope.js";
import { bookingFailedCopy } from "./home.js";
import type { CompanySlug, RouteName, StaffRole } from "../types.js";

export interface MonthsPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /** The role the shell already knows (B2a-1); never re-read from the bus. */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** Clock for the shared "6 minutes ago" phrases (tests pin it). */
  now?: () => Date;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1). A month that names one mismatched
   * statement account names the company, never its internal code.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

const STATUS_TEXT: Record<MonthStatus, string> = {
  closed: "Closed",
  closed_check_failed: "Closed, check failed",
  reopened: "Reopened",
  needs_you: "Needs you",
  waiting: "Waiting",
  open: "Open",
};

/** Chip tone uses the existing F1/SH chip classes. */
const STATUS_TONE: Record<MonthStatus, string> = {
  closed: "ok",
  closed_check_failed: "crit",
  reopened: "warn",
  needs_you: "warn",
  waiting: "info",
  open: "info",
};

const NOT_AVAILABLE = "Not available";

/** Own-property lookup: a server value like "constructor" must not match. */
function known<T>(table: Readonly<Record<string, T>>, key: unknown): T | undefined {
  if (typeof key !== "string") return undefined;
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}

function statusText(status: MonthStatus): string {
  return known(STATUS_TEXT, status) ?? NOT_AVAILABLE;
}

function statusTone(status: MonthStatus): string {
  return known(STATUS_TONE, status) ?? "info";
}

/**
 * The chip's colour, from the word it shows (rd r12 F1, R6). The word comes
 * from `monthAction`, which names the first blocker, so a closed month can
 * read "Needs you"; colouring it by the raw status painted that word green.
 * Only "Closed" is green, a failed check after close stays the alarm colour,
 * a word that asks for a person is a warning, and every wait is information.
 */
function chipTone(status: MonthStatus, word: string): string {
  if (!isKnownStatus(status)) return statusTone(status);
  if (status === "closed_check_failed") return statusTone(status);
  if (word === "Closed") return "ok";
  if (word === "Needs you" || word === "Needs closing again") return "warn";
  return "info";
}

function isKnownStatus(status: MonthStatus): boolean {
  return known(STATUS_TEXT, status) !== undefined;
}

/** Frozen target -> portal route. The target is server-sent; never the key. */
const TARGET_ROUTE: Record<MonthBlockerTarget, RouteName | null> = {
  to_review: "queue",
  waiting: "waiting",
  statement_checks: "statements",
  problems: "errors",
  none: null,
};

const TARGET_BUTTON: Record<MonthBlockerTarget, string | null> = {
  to_review: `Open ${navLabel("queue")}`,
  waiting: `Open ${navLabel("waiting")}`,
  statement_checks: `Open ${navLabel("statements")}`,
  problems: `Open ${navLabel("errors")}`,
  none: null,
};

/**
 * Which cards a `to_review` blocker line counted, or null. The Worker counts
 * each of these as a separate set of the month's cards; the link must hand
 * over the set, not just the month, or "4 cards to review" opens a list of 6
 * when 2 cards are escalated (round 2, MUST-FIX 1).
 */
const REVIEW_SET_FOR_KEY: Readonly<Partial<Record<MonthBlockerKey, ReviewCardSet>>> = {
  cards_to_review: "cards_to_review",
  cards_with_owner: "cards_with_owner",
  close_blocked: "close_blocked",
  closed_month_write_blocked: "closed_month_write_blocked",
  reclose_needed: "reclose_needed",
};

/** A set whose cards are named by a month's lifecycle subject (needs a month). */
const PERIOD_BOUND_SETS: ReadonlySet<ReviewCardSet> = new Set<ReviewCardSet>([
  "close_blocked",
  "closed_month_write_blocked",
  "reclose_needed",
]);

function isKnownBlockerKey(key: string): key is MonthBlockerKey {
  return (MONTH_BLOCKER_KEYS as readonly string[]).includes(key);
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * A time the way the rest of the console says it (rd-polish-rules §1): a plain
 * phrase from `timeAgoText`, never an ISO stamp and never the word UTC. Home
 * says "6 minutes ago" about the same run, so Months must too (C-11 LOW).
 */
function timePhrase(iso: string | undefined, now: Date): string {
  if (iso === undefined) return "at an unknown time";
  const phrase = timeAgoText(iso, now);
  return phrase === "" ? "at an unknown time" : phrase;
}

function monthLabel(period: string): string {
  return amVisibleDisplayText(formatPeriodLabel(amVisibleDisplayText(period)));
}

function reconciledPhrase(month: AdminMonth): string {
  const reconciled: MonthReconciled = month.reconciled;
  if (reconciled === "yes") return "Reconciled";
  if (reconciled === "no") return "A bank balance does not match";
  const checked = month.accountsChecked;
  if (!Number.isInteger(checked) || checked <= 0) return "Not reconciled yet";
  return `Not reconciled yet \u00b7 ${plural(checked, "bank statement", "bank statements")} checked so far`;
}

function isClosedStatus(status: MonthStatus): boolean {
  return status === "closed" || status === "closed_check_failed";
}

/**
 * What the month is waiting on, from the one owner of that fact (`monthAction`).
 * The closed day is the only page-local addition: a timestamp prints the
 * reader's calendar day (round-11 item 25), never a UTC-shaped day.
 */
function summarySentence(month: AdminMonth, role: StaffRole): string | null {
  if (!isKnownStatus(month.status)) return null;
  const action = monthAction(month, role);
  if (month.status === "closed") {
    const day = month.closedAt === undefined ? "" : dateText(month.closedAt);
    return day === "" ? action.sentence : `Closed ${day}.`;
  }
  return action.sentence;
}

const BLOCKER_SENTENCE: Record<MonthBlockerKey, (count: number, role: StaffRole) => string> = {
  close_blocked: () => "The month close was stopped. Open the card to see why.",
  reclose_needed: () =>
    "The month-end close closes this month again. You cannot close it from here.",
  closed_month_write_blocked: (count) =>
    `${plural(count, "booking", "bookings")} tried to change this closed month.`,
  cards_to_review: (count) => `${plural(count, "card", "cards")} to review.`,
  cards_with_owner: (count, role) =>
    role === "owner"
      ? `${plural(count, "escalated card", "escalated cards")} need${count === 1 ? "s" : ""} you.`
      : `${plural(count, "card", "cards")} ${count === 1 ? "is" : "are"} with the owner.`,
  cards_with_practice: (count) =>
    `${plural(count, "card", "cards")} ${count === 1 ? "is" : "are"} with the hub.`,
  statements_not_matching: (count, role) =>
    `${plural(count, "bank statement", "bank statements")} ${
      count === 1 ? "does" : "do"
    }${sentence("statements_not_match_tail", role)}`,
  statements_to_confirm: (count) =>
    `${plural(count, "bank statement", "bank statements")} ${count === 1 ? "needs" : "need"} checking.`,
  statements_saving: (count) =>
    `${plural(count, "statement check", "statement checks")} will be saved on the next booking run.`,
  check_failed_after_close: () => "A balance check failed after closing.",
  bank_lines_waiting: (count) =>
    `${plural(count, "bank line", "bank lines")} will be booked on the next run.`,
  bank_lines_failed: (count) =>
    `${plural(count, "bank line", "bank lines")} could not be booked.`,
};

function statementMismatchSentence(account: string, role: StaffRole): string {
  // The shared sentence (docs/design/rd-z-fixes-background.md §3): one account,
  // named the way Statements names it, and the page named by the registry.
  return sentence("statement_mismatch_one_liner", role, { account });
}

function blockerButton(
  blocker: AdminMonthBlocker,
  role: StaffRole,
  scope: { company: CompanySlug; month: string | null },
): HTMLButtonElement | null {
  const label = known(TARGET_BUTTON, blocker.target);
  const route = known(TARGET_ROUTE, blocker.target);
  if (label == null || route == null) return null;
  const set = known(REVIEW_SET_FOR_KEY, blocker.key) ?? null;
  // The account manager cannot see the owner's cards (To review leaves them
  // out), so a link to that set would open a list that cannot contain them.
  // The words above already say they are with the owner; there is no button.
  if (set === "cards_with_owner" && role === "account_manager") return null;
  // A lifecycle set is named by that month's subject, so it needs a month to
  // point at. The Worker never leaves such a card undated, so this is only a
  // guard against a hand-made link that could not be honoured.
  if (set !== null && PERIOD_BOUND_SETS.has(set) && scope.month === null) return null;
  const control = button(label, "secondary", {
    "data-action": "month-blocker",
    "data-target": blocker.target,
  });
  control.addEventListener("click", () => {
    // A link that counts a subset hands that scope over (round 10, C-4): the
    // company, the month the button sits under, and WHICH cards were counted
    // (round 2, MUST-FIX 1). Other targets keep their own navigation and
    // leave no hand-off behind.
    if (route === "queue") {
      setReviewScopeHint({
        company: scope.company,
        month: scope.month,
        set,
      });
    }
    navigateTo(route);
  });
  return control;
}

function blockerLine(
  blocker: AdminMonthBlocker,
  role: StaffRole,
  scope: { company: CompanySlug; month: string | null },
): HTMLElement | null {
  if (!isKnownBlockerKey(blocker.key)) return null;
  const count = Number.isInteger(blocker.count) && blocker.count > 0 ? blocker.count : 1;
  const line = h("div", {
    class: "actions",
    "data-role": "month-blocker",
    "data-key": blocker.key,
    "data-count": String(count),
  });
  line.append(
    h("p", { class: "month-blocker-text", "data-role": "month-blocker-text" }, [
      BLOCKER_SENTENCE[blocker.key](count, role),
    ]),
  );
  const control = blockerButton(blocker, role, scope);
  if (control !== null) line.append(control);
  return line;
}

/**
 * The account label for a single mismatch, taken from the same row the
 * Statements page reads. The set helper is the console's one way to name an
 * account beside its twins (rd-polish-rules §1; Y0's `accountLabel`); a month
 * with several mismatches has no single name to give, so it keeps the count.
 */
async function loadSingleMismatchAccount(
  client: AdminClient,
  company: CompanySlug,
  period: string,
  companies: AccountLabelContext,
): Promise<string | null> {
  try {
    const body = await client.listStatementReconciliations(company, period);
    const mismatches = body.items.filter((row) => row.outcome === "mismatch");
    if (mismatches.length !== 1) return null;
    const account = mismatches[0]?.account;
    if (account === undefined || account === "") return null;
    // The set builder is the console's one way to name an account beside its
    // twins; it always has a label for a member (`accountLabel` is its
    // fallback inside), so this never returns a raw path.
    return (
      accountLabelSet(
        mismatches.map((row) => row.account),
        companies,
      ).get(account) ?? null
    );
  } catch {
    return null;
  }
}

/**
 * Paint one blocker list. Unknown keys are counted in a hidden hook and never
 * rendered: a future server key must not become a code on screen.
 */
function paintBlockers(
  host: HTMLElement,
  blockers: readonly AdminMonthBlocker[],
  role: StaffRole,
  scope: { company: CompanySlug; month: string | null },
): number {
  let unknown = 0;
  for (const blocker of blockers) {
    if (!isKnownBlockerKey(blocker.key)) {
      unknown += 1;
      continue;
    }
    const line = blockerLine(blocker, role, scope);
    if (line !== null) host.append(line);
  }
  return unknown;
}

function unknownHook(count: number): HTMLElement {
  return h("span", {
    hidden: "",
    "data-role": "month-unknown-blockers",
    "data-count": String(count),
  });
}

/** The company-wide pending-card count and how many server keys it could not name. */
interface PendingCards {
  /** Cards that To review's shared list can contain. */
  total: number;
  /** Blocker keys this build does not know, so `total` may be short. */
  unknown: number;
}

/** A positive count from the server, or zero (never a negative total). */
function positiveCount(count: number): number {
  return Number.isInteger(count) && count > 0 ? count : 0;
}

/**
 * How many pending cards this company has in the months this response covers.
 * To review lists the oldest 500 across the company, so a month's own count is
 * not the right measure of whether its list can be cut off (round 11, R7-18).
 *
 * The server names the destination (`target`), so the count follows the target
 * instead of a hand-kept key list: a `to_review` key this build has never heard
 * of is still one card in that list (round 2, SHOULD-FIX 4). `cards_with_practice`
 * needs no exclusion of its own — its target is `waiting`, and the Worker's
 * 500-row list never contains those.
 */
function companyPendingCards(data: AdminMonthsResponse, role: StaffRole): PendingCards {
  const pending: PendingCards = { total: 0, unknown: 0 };
  const add = (blocker: AdminMonthBlocker): void => {
    if (!isKnownBlockerKey(blocker.key)) pending.unknown += 1;
    if (blocker.target !== "to_review") return;
    // The account manager cannot open the owner's cards and To review leaves
    // them out, so they cannot shorten the AM's list.
    if (role === "account_manager" && blocker.key === "cards_with_owner") return;
    pending.total += positiveCount(blocker.count);
  };
  for (const blocker of data.undatedBlockers) add(blocker);
  for (const month of data.months) {
    for (const blocker of month.blockers) add(blocker);
  }
  return pending;
}

function cardsToReviewNote(
  blockers: readonly AdminMonthBlocker[],
  pending: PendingCards,
): HTMLElement | null {
  const cards = blockers.find(
    (blocker) => isKnownBlockerKey(blocker.key) && blocker.key === "cards_to_review",
  );
  if (cards === undefined || positiveCount(cards.count) === 0) return null;
  // An unknown blocker key is counted as a floor: the count may be short, so
  // the note says so even under the cap.
  if (pending.total < FLEET_QUEUE_LIST_LIMIT && pending.unknown === 0) return null;
  return h("p", { class: "muted", "data-role": "month-cards-note" }, [
    `${navLabel("queue")} shows up to ${String(FLEET_QUEUE_LIST_LIMIT)} cards, so this month's list may be shorter than this count.`,
  ]);
}

function booksNotes(month: AdminMonth, now: Date): readonly HTMLElement[] {
  const notes: HTMLElement[] = [];
  if (month.booksView === "not_loaded") {
    notes.push(
      h("p", { class: "muted", "data-role": "month-books-not-loaded" }, [
        "Books not loaded for this month yet.",
      ]),
    );
  } else if (month.booksAsOf !== undefined) {
    notes.push(
      h("p", { class: "muted", "data-role": "month-books-as-of" }, [
        `Books as of ${timePhrase(month.booksAsOf, now)}.`,
      ]),
    );
  }
  if (isKnownStatus(month.status) && !isClosedStatus(month.status)) {
    notes.push(
      h("p", { class: "muted", "data-role": "month-provisional" }, [
        "This month is not closed or reconciled yet.",
      ]),
    );
  }
  return notes;
}

function renderMonth(
  month: AdminMonth,
  index: number,
  role: StaffRole,
  company: CompanySlug,
  now: Date,
  client: AdminClient,
  stillCurrent: () => boolean,
  pending: PendingCards,
  companies: AccountLabelContext,
): HTMLElement {
  const period = month.period;
  const label = monthLabel(period);
  const scope = { company, month: period };
  const panelId = `months-blockers-${String(index)}`;
  const wrapper = h("section", { class: "card", "data-role": "month-card", "data-period": period });

  const action = monthAction(month, role);
  const chipWord = isKnownStatus(month.status) ? action.word : statusText(month.status);
  const toggle = h("button", {
    type: "button",
    class: "list-row",
    "data-role": "month-row",
    "data-period": period,
    "data-status": isKnownStatus(month.status) ? month.status : "unknown",
    "data-action": "month-toggle",
    "aria-expanded": "false",
    "aria-controls": panelId,
    "aria-label": `${label}, ${chipWord}`,
  });
  toggle.append(
    h("span", { class: "actions", "data-role": "month-headline" }, [
      h("span", { class: "month-name", "data-role": "month-name" }, [label]),
      chip(chipTone(month.status, chipWord), chipWord),
    ]),
  );
  toggle.append(
    h("span", { class: "list-row-detail", "data-role": "month-reconciled" }, [
      reconciledPhrase(month),
    ]),
  );
  const summary = summarySentence(month, role);
  if (summary !== null) {
    toggle.append(
      h("span", { class: "list-row-detail", "data-role": "month-summary" }, [summary]),
    );
  }
  // A row that opens is a row that looks like it opens (rd Y3): one chevron at
  // the row's trailing edge that turns when the blockers are shown. It is the
  // row's LAST child and carries no text, and `.month-chevron` (ledger.css, the
  // lane's own file) takes it out of the column flow so the row cannot grow a
  // line. styles.css is Y4's, so the one rule lives beside the other Books
  // list styles.
  const chevron = h("span", {
    class: "month-chevron",
    "data-role": "month-chevron",
    "data-open": "false",
    "aria-hidden": "true",
  }, [svgIcon("chevron")]);
  toggle.append(chevron);

  const panel = h("div", {
    class: "months-blockers",
    id: panelId,
    "data-role": "month-blockers",
    hidden: "",
  });
  const unknown = paintBlockers(panel, month.blockers, role, scope);
  if (unknown > 0) panel.append(unknownHook(unknown));
  const note = cardsToReviewNote(month.blockers, pending);
  if (note !== null) panel.append(note);
  for (const bookNote of booksNotes(month, now)) panel.append(bookNote);

  // One mismatching statement, one account name (C-11). The Months response
  // carries counts only, so the name comes from the same read Statements makes
  // — ONE read, on the first expand of that row, never at load (round 2,
  // MUST-FIX 4a: three mismatched months used to cost three reads nobody
  // asked for). A plural count has no single name to give and is not read.
  let mismatchRequested = false;
  const loadMismatchAccount = async (): Promise<void> => {
    if (mismatchRequested) return;
    mismatchRequested = true;
    // A superseded page stops reading: no later month starts a read whose
    // result would be dropped.
    if (!stillCurrent()) return;
    const account = await loadSingleMismatchAccount(client, company, period, companies);
    if (account === null || !stillCurrent()) return;
    const line = panel.querySelector<HTMLElement>(
      '[data-role="month-blocker"][data-key="statements_not_matching"] [data-role="month-blocker-text"]',
    );
    if (line === null) return;
    clear(line);
    line.append(document.createTextNode(statementMismatchSentence(account, role)));
  };
  const hasSingleMismatch = month.blockers.some(
    (blocker) =>
      isKnownBlockerKey(blocker.key) &&
      blocker.key === "statements_not_matching" &&
      blocker.count === 1,
  );

  toggle.addEventListener("click", () => {
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", expanded ? "false" : "true");
    panel.hidden = expanded;
    chevron.setAttribute("data-open", expanded ? "false" : "true");
    if (!expanded && hasSingleMismatch) void loadMismatchAccount();
  });

  wrapper.append(toggle, panel);
  return wrapper;
}

function runLine(
  run: AdminMonthsLastBookingRun | null,
  now: Date,
  role: StaffRole,
): HTMLElement {
  const line = h("p", { class: "muted", "data-role": "months-run" });
  if (run === null) {
    line.append(document.createTextNode("Booking has not run for this company yet."));
    return line;
  }
  switch (run.outcome) {
    case "running":
      line.append(document.createTextNode(sentence("booking_running", role)));
      return line;
    case "succeeded": {
      const at = run.completedAt ?? run.startedAt;
      line.append(document.createTextNode(`Booking last ran ${timePhrase(at, now)}.`));
      return line;
    }
    case "failed": {
      const at = run.completedAt ?? run.startedAt;
      // Round 11 (R7-17): Home and Months say the same thing about the same
      // failed run. The helper owns the sentence; this page keeps the time and
      // the way to Connection status.
      line.append(
        document.createTextNode(
          `Last run ${timePhrase(at, now)}. ${bookingFailedCopy(role)} `,
        ),
      );
      const see = button(`See ${navLabel("health")}`, "quiet", {
        "data-action": "months-health",
      });
      see.addEventListener("click", () => {
        navigateTo("health");
      });
      line.append(see);
      return line;
    }
  }
}

/**
 * The read failure's words. The 401, 403 and 429 branches are the one home's
 * (`failureCopy`), so decision 1's owner sentence and the round-12 table reach
 * this page; the page keeps its own connection-free fallback and its retry
 * rule (401 and 403 cannot pass on a retry; 429 can).
 */
function failureSentence(
  error: unknown,
  role: StaffRole,
): { sentence: string; retry: boolean } {
  const status = error instanceof ConsoleDataError ? error.status : 0;
  if (status === 401 || status === 403 || status === 429) {
    return {
      sentence: failureCopy(error, { role, context: "read" }).sentence,
      retry: status === 429,
    };
  }
  return { sentence: "Months could not load. Try again.", retry: true };
}

function failureState(opts: MonthsPageOptions, error: unknown, role: StaffRole): HTMLElement {
  const { sentence, retry } = failureSentence(error, role);
  const body = h("div", {
    class: "error-state",
    role: "alert",
    "data-role": "months-error",
  }, [
    h("h2", { class: "error-state-title" }, ["We could not load this page"]),
    h("p", { "data-role": "months-error-copy" }, [sentence]),
  ]);
  if (retry) {
    const again = button("Try again", "primary", {
      "data-action": "months-retry",
      "aria-label": "Try again",
    });
    again.addEventListener("click", () => {
      void renderMonthsPage(opts);
    });
    body.append(again);
  }
  return body;
}

function skeleton(): HTMLElement {
  return h("div", { "data-role": "months-loading", "aria-label": "Loading months" }, [
    h("span", { class: "skeleton-line" }),
    h("span", { class: "skeleton-line" }),
    h("span", { class: "skeleton-line" }),
  ]);
}

export async function renderMonthsPage(opts: MonthsPageOptions): Promise<void> {
  const { client, company, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const now = opts.now?.() ?? new Date();
  // The shell passes the role it already knows (B2a-1). Without one, owner
  // decision 10 applies: unknown reads as the account manager.
  const role: StaffRole = readerRole(opts.role);
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  clear(root);

  const page = h("div", { class: "settings-stack" });
  root.append(page);
  page.append(
    pageHeader("Months", "Closed, reconciled, or what is blocking each month."),
  );

  if (company === "") {
    page.append(
      h("p", { class: "empty-state", "data-role": "months-no-company" }, [
        "Pick a company to see its months.",
      ]),
    );
    return;
  }

  const body = h("div", { "data-role": "months-body" });
  page.append(body);
  body.append(skeleton());

  let data: AdminMonthsResponse;
  try {
    data = await client.getMonths(company);
  } catch (error) {
    if (!stillCurrent()) return;
    clear(body);
    body.append(failureState(opts, error, role));
    return;
  }
  if (!stillCurrent()) return;

  clear(body);
  body.append(runLine(data.lastBookingRun, now, role));

  if (data.undatedBlockers.length > 0) {
    // Decision 9: the Worker counts cards outside the 24-month window, before
    // the books start, or in the future in `undatedBlockers`.
    const undated = h("section", {
      class: "card",
      "data-role": "undated-blockers",
    }, [
      h("h2", { class: "section-title" }, [
        sentence("outside_months_shown", role),
      ]),
    ]);
    const unknown = paintBlockers(undated, data.undatedBlockers, role, {
      company,
      month: null,
    });
    if (unknown > 0) undated.append(unknownHook(unknown));
    body.append(undated);
  }

  if (data.months.length === 0) {
    body.append(
      h("p", { class: "empty-state", "data-role": "months-empty" }, [
        "No months to show yet.",
      ]),
    );
    return;
  }

  const list = h("div", { "data-role": "months-list" });
  const pending = companyPendingCards(data, role);
  data.months.forEach((month, index) => {
    list.append(
      renderMonth(month, index, role, company, now, client, stillCurrent, pending, companies),
    );
  });
  body.append(list);
}

